/**
 * useControlledMutations —— 受控写回 handler 集合（引擎的「写」侧）。
 *
 * 【职责边界】只负责「把用户操作翻译成一次不可变 onChange(next)」。不含：
 *  - 状态管理（`forceValidate` / `overscanMode` 等留在容器）；
 *  - 校验（见 `fieldRules` + `useErrorLocator`）；
 *  - 渲染（见 `renderCells` / `buildBedrockColumns`）。
 *
 * 【⚠️ 本文件最危险的一条硬规则：每个 handler 必须逐个 `useMemoizedFn`】
 * `useMemoizedFn` 的 identity **永久稳定**（与闭包捕获了什么无关），故：
 *  - 调用方把这些 handler 直接放进 `bedrockColumns` 的 useMemo 依赖数组是**安全**的
 *    （见 `scripts/check-deps-array.mjs` 冻结的 10 项）；
 *  - 若改用 `useCallback` 或裸箭头 → identity 每次渲染都变 → `bedrockColumns` 重建
 *    → @bedrock table 三级 memo 全线失效。**表现为「变卡 + 输入框失焦」，不报错、不崩溃，
 *    是最难归因的一类静默回归**（历史基线：1290ms → 38ms 就是靠这套 identity 拿回来的）。
 *
 * 【为何 `markEditing` 由外部注入而不在本文件内实现】
 * 它驱动的是 `overscanMode`（视窗高 state），属于**容器**的渲染关注点；本文件只消费它。
 */
import { useMemoizedFn } from 'ahooks';
import { isEmpty } from './fieldRules';
import type { ChildBase, ChildId, RowBase, RowId, SubArraySpec } from './types';

export interface UseControlledMutationsParams<TRow extends RowBase, TChild extends ChildBase> {
  /** 受控主行数组（整棵复杂结构，含各子数组） */
  value: TRow[];
  onChange: (next: TRow[]) => void;
  subArrays: SubArraySpec<TRow, TChild>[];
  /** 标记「用户正在编辑」：用于切到小窗口 overscan（促输入），由容器提供 */
  markEditing: () => void;
}

export interface UseControlledMutationsResult<TRow extends RowBase> {
  updateRow: (id: RowId, patch: Partial<TRow>) => void;
  patchItem: (rowId: RowId, subKey: string, childId: ChildId, p: Record<string, unknown>) => void;
  addItem: (rowId: RowId, subKey: string) => void;
  requestAddItem: (row: TRow, subKey: string) => void;
  reorderItems: (rowId: RowId, subKey: string, fromIndex: number, toIndex: number) => void;
  removeItem: (rowId: RowId, subKey: string, childId: ChildId) => void;
  handleCopyRow: (id: RowId) => void;
  handleDeleteRow: (id: RowId) => void;
  handleBatchApply: (field: string, value: string, mode: 'keep' | 'cover') => void;
}

export function useControlledMutations<TRow extends RowBase, TChild extends ChildBase>({
  value,
  onChange,
  subArrays,
  markEditing,
}: UseControlledMutationsParams<TRow, TChild>): UseControlledMutationsResult<TRow> {
  /** 主行字段写回（doc 列 / 批量编辑的标量路径） */
  const updateRow = useMemoizedFn((id: RowId, patch: Partial<TRow>) => {
    markEditing();
    onChange(value.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  });

  /** 子项编辑：根据 subKey 定位子数组，不可变改子项 */
  const patchItem = useMemoizedFn((rowId: RowId, subKey: string, childId: ChildId, p: Record<string, unknown>) => {
    markEditing();
    const spec = subArrays.find((s) => s.key === subKey);
    if (!spec) return;
    onChange(value.map((r) => (r.id === rowId ? spec.setItems(r, spec.getItems(r).map((it) => (it.id === childId ? { ...it, ...p } : it))) : r)));
  });

  /** 追加一个默认子项（不经弹窗的直插路径） */
  const addItem = useMemoizedFn((rowId: RowId, subKey: string) => {
    markEditing();
    const spec = subArrays.find((s) => s.key === subKey);
    if (!spec) return;
    onChange(value.map((r) => (r.id === rowId ? spec.setItems(r, [...spec.getItems(r), spec.makeDefault()]) : r)));
  });

  /**
   * "+ 添加{emptyLabel}"点击入口：若 subArraySpec 声明了 onAddRequest（如弹窗选择关联权益），
   * 转交业务方处理，业务方异步完成后调用 commit(items) 由引擎不可变追加写回；
   * 否则沿用默认行为（push(makeDefault())）。
   */
  const requestAddItem = useMemoizedFn((row: TRow, subKey: string) => {
    const spec = subArrays.find((s) => s.key === subKey);
    if (!spec) return;
    if (spec.onAddRequest) {
      spec.onAddRequest(row, (items: TChild[]) => {
        if (!items?.length) return;
        markEditing();
        onChange(value.map((r) => (r.id === row.id ? spec.setItems(r, [...spec.getItems(r), ...items]) : r)));
      });
      return;
    }
    addItem(row.id, subKey);
  });

  /** 子项拖拽重排：把 fromIndex 位置的子项移动到 toIndex，不可变写回该行该子数组。 */
  const reorderItems = useMemoizedFn((rowId: RowId, subKey: string, fromIndex: number, toIndex: number) => {
    const spec = subArrays.find((s) => s.key === subKey);
    if (!spec) return;
    markEditing();
    onChange(value.map((r) => {
      if (r.id !== rowId) return r;
      const items = spec.getItems(r);
      if (fromIndex < 0 || fromIndex >= items.length || toIndex < 0 || toIndex >= items.length) return r;
      const next = items.slice();
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return spec.setItems(r, next);
    }));
  });

  /** 删除单个子项 */
  const removeItem = useMemoizedFn((rowId: RowId, subKey: string, childId: ChildId) => {
    markEditing();
    const spec = subArrays.find((s) => s.key === subKey);
    if (!spec) return;
    onChange(value.map((r) => (r.id === rowId ? spec.setItems(r, spec.getItems(r).filter((x) => x.id !== childId)) : r)));
  });

  /**
   * 复制主行：插到源行之后，并给副本及其所有子项换新 id。
   *
   * ⚠️ 子项 id 用 `${原id}-c${ts}` 而非全新生成 —— 保留「从哪条复制来」的可追溯性，
   * 同时靠时间戳后缀保证同一行被复制多次也不撞 id。
   */
  const handleCopyRow = useMemoizedFn((id: RowId) => {
    const idx = value.findIndex((r) => r.id === id);
    if (idx < 0) return;
    const src = value[idx];
    const ts = Date.now();
    const copy: TRow = {
      ...src,
      id: `copy-${ts}`,
    } as TRow;
    for (const spec of subArrays) {
      (copy as unknown as Record<string, unknown>)[spec.key] = spec.getItems(src).map((it) => ({ ...it, id: `${it.id}-c${ts}` }));
    }
    const next = [...value];
    next.splice(idx + 1, 0, copy);
    onChange(next);
  });

  /** 删除主行（子数组随主行一并消失，无需单独处理） */
  const handleDeleteRow = useMemoizedFn((id: RowId) => onChange(value.filter((r) => r.id !== id)));

  /**
   * 批量编辑写回（标量路径）：`keep` 只填空白格，`cover` 覆盖全部。
   *
   * ⚠️ 判空用 `isEmpty`（只判 undefined/null/空串，**不含空数组**）—— 与校验侧同源。
   * custom 列（如属性列的 `property_values`）不走这里，由 `batchEdit.apply` 自行写回。
   */
  const handleBatchApply = useMemoizedFn((field: string, bv: string, mode: 'keep' | 'cover') => {
    onChange(value.map((r) => {
      if (mode === 'keep') return isEmpty(r[field]) ? { ...r, [field]: bv } : r;
      return { ...r, [field]: bv };
    }));
  });

  return {
    updateRow,
    patchItem,
    addItem,
    requestAddItem,
    reorderItems,
    removeItem,
    handleCopyRow,
    handleDeleteRow,
    handleBatchApply,
  };
}
